/**
 * Per-tab impersonation bootstrap. The exported `runImpersonationBootstrap` runs
 * at import time (see the self-invocation at the bottom), BEFORE the auth store is
 * constructed, so the store's persist storage selector can pick sessionStorage for
 * an impersonation tab.
 *
 * Flow: a staff user opens `/login/impersonate#data=<urlenc base64 JSON>` in a NEW
 * tab. The hash carries `{ t: accessToken, subject }` (the player store's subject
 * shape). We:
 *   1. mark this tab as an impersonation tab (`b1fr0st.player.impersonation` flag),
 *   2. seed the zustand-persist envelope into sessionStorage under the player store
 *      key `b1fr0st.player.auth` (NOT localStorage — that is shared across tabs and
 *      would clobber a real member's own session),
 *   3. strip the token from the URL so it never lands in history / referrer.
 *
 * Everything is wrapped in try/catch — a malformed hash must never crash boot.
 */
import { storageKey } from './storage-key.js';

export const IMPERSONATION_FLAG = storageKey('impersonation');
const AUTH_STORAGE_KEY = storageKey('auth');
const AUTH_PERSIST_VERSION = 0;

/** True when this tab was seeded by a staff "view as member" hand-off. */
export function isImpersonationTab(): boolean {
  return (
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem(IMPERSONATION_FLAG) === '1'
  );
}

/**
 * Storage selector for the player auth store's persist middleware. Impersonation
 * tabs keep their session in sessionStorage (per-tab) so they never clobber a real
 * member's localStorage session, which is shared across every tab of the origin.
 */
export function impersonationStorage(): Storage {
  return isImpersonationTab() ? sessionStorage : localStorage;
}

export function runImpersonationBootstrap(): void {
  try {
    if (
      typeof window === 'undefined' ||
      window.location.pathname !== '/login/impersonate' ||
      window.location.hash.length <= 1
    ) {
      return;
    }
    const params = new URLSearchParams(window.location.hash.slice(1));
    const raw = params.get('data');
    if (!raw) return;

    const decoded = JSON.parse(atob(decodeURIComponent(raw))) as {
      t?: string;
      subject?: unknown;
    };
    if (!decoded.t || !decoded.subject) return;

    sessionStorage.setItem(IMPERSONATION_FLAG, '1');
    sessionStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        state: {
          accessToken: decoded.t,
          refreshToken: null,
          subject: decoded.subject,
        },
        version: AUTH_PERSIST_VERSION,
      }),
    );
    window.history.replaceState(null, '', '/');
  } catch {
    // Never throw at boot — a bad hash just means no impersonation session.
  }
}

runImpersonationBootstrap();
